import { useTranslation } from 'react-i18next';
import SEO from '../../components/base/SEO';

export default function AboutPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://braincommerce.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About BrainCommerce",
    "description": "We believe work should not be replaced, but redesigned. Learn about our philosophy and vision for the future of work.",
    "url": `${siteUrl}/about`,
    "mainEntity": {
      "@type": "Organization",
      "name": "BrainCommerce",
      "description": "BrainCommerce integrates human expertise, AI intelligence, and robotic automation to redesign the future of work."
    }
  };

  return (
    <>
      <SEO
        title="About BrainCommerce - Redesigning the Future of Work | Philosophy & Vision"
        description="We believe work should not be replaced, but redesigned. BrainCommerce integrates human expertise, AI intelligence, and robotic automation to create a new workforce paradigm."
        keywords="work redesign, workforce philosophy, hybrid workforce vision, BrainCommerce philosophy, future of work, human AI collaboration"
        canonical={`${siteUrl}/about`}
        schema={schema}
      />
      
      <div className="min-h-screen bg-white">
        {/* Opening Statement */}
        <section className="relative min-h-screen flex items-center px-4 md:px-8 lg:px-32 py-24 md:py-32">
          <div className="absolute top-24 md:top-32 left-4 md:left-8 lg:left-32">
            <span className="text-xs uppercase tracking-[0.2em] text-[#4a4a4a]">/PHILOSOPHY</span>
          </div>
          
          <div className="w-full">
            <div className="ml-auto w-full md:w-4/5 lg:w-3/5 text-left md:text-right">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[52px] font-extralight leading-[2.5] text-[#1a1a1a] mb-12 md:mb-48">
                우리는 일이 대체되는 것이 아니라<br />
                재설계되어야 한다고 믿습니다
              </h1>
            </div>
            
            <div className="flex items-center gap-3 md:gap-4 mt-auto">
              <div className="flex">
                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 -mr-2 md:-mr-3 border-2 md:border-4 border-white"></div>
                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 -mr-2 md:-mr-3 border-2 md:border-4 border-white"></div>
                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-gray-500 to-gray-600 -mr-2 md:-mr-3 border-2 md:border-4 border-white"></div>
                <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 border-2 md:border-4 border-white"></div>
              </div>
              <p className="text-xs md:text-sm text-[#4a4a4a]">{t('about.opening.team')}</p>
            </div>
          </div>
        </section>

        {/* Vision Blocks */}
        <section className="space-y-16 md:space-y-32 mb-16 md:mb-32">
          {/* Block 1 */}
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-1/2">
              <img 
                src="https://public.readdy.ai/ai/img_res/2865db13020b8bab6c32b3870d312759.jpg"
                alt="Human and Robot Collaboration"
                className="w-full h-[400px] md:h-[600px] object-cover object-top"
              />
            </div>
            <div className="w-full md:w-1/2 flex items-center px-6 md:px-12 lg:px-20 py-8 md:py-0">
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-[#1a1a1a] mb-4 md:mb-6">{t('about.vision.why.title')}</h2>
                <p className="text-base md:text-lg text-[#4a4a4a] leading-relaxed">
                  {t('about.vision.why.content')}
                </p>
              </div>
            </div>
          </div>

          {/* Block 2 - Reversed */}
          <div className="flex flex-col-reverse md:flex-row">
            <div className="w-full md:w-1/2 flex items-center px-6 md:px-12 lg:px-20 py-8 md:py-0">
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-[#1a1a1a] mb-4 md:mb-6">{t('about.vision.broken.title')}</h2>
                <p className="text-base md:text-lg text-[#4a4a4a] leading-relaxed">
                  {t('about.vision.broken.content')}
                </p>
              </div>
            </div>
            <div className="w-full md:w-1/2">
              <img 
                src="https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20interconnected%20neural%20networks%20and%20geometric%20patterns%20representing%20artificial%20intelligence%20and%20data%20flow%20in%20shades%20of%20white%20gray%20and%20subtle%20blue%20with%20clean%20minimalist%20aesthetic%20showing%20technological%20sophistication%20and%20digital%20transformation&width=800&height=900&seq=about002&orientation=portrait"
                alt="Technology Visualization"
                className="w-full h-[400px] md:h-[600px] object-cover object-top"
              />
            </div>
          </div>

          {/* Block 3 - Full Width Statement */}
          <div className="px-4 md:px-8 lg:px-32 py-12 md:py-24">
            <div className="max-w-[960px] mx-auto text-center">
              <p className="text-xl md:text-2xl lg:text-[28px] font-serif text-[#1a1a1a] leading-relaxed">
                {t('about.vision.manifesto')}
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
